import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { X, Star, Eye, EyeOff } from "lucide-react";

interface Beat {
  id: string;
  title: string;
  producer: string;
  image_url: string;
  is_featured: boolean;
  is_hidden?: boolean;
  mp3_lease_price: number;
  wav_lease_price: number;
  flp_price: number;
  semi_exclusive_price: number;
  full_exclusive_price: number;
}

interface AdminFeaturedBeatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBeatsUpdated?: () => void;
}

export const AdminFeaturedBeatsModal = ({ isOpen, onClose, onBeatsUpdated }: AdminFeaturedBeatsModalProps) => {
  const [beats, setBeats] = useState<Beat[]>([]);
  const [featuredBeats, setFeaturedBeats] = useState<string[]>([]);
  const [licenseTypes, setLicenseTypes] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchBeats();
    }
  }, [isOpen]);

  const fetchBeats = async () => {
    const { data: beatsData } = await supabase
      .from("beats")
      .select("*")
      .order("created_at", { ascending: false });
    
    if (beatsData) {
      setBeats(beatsData);
      const featured = beatsData.filter(beat => beat.is_featured).map(beat => beat.id);
      setFeaturedBeats(featured);
      
      // Fetch existing license preferences
      const { data: licenseData } = await supabase
        .from("beat_license_preferences")
        .select("*");
      
      // Initialize license types based on stored preferences or defaults
      const initialLicenseTypes: Record<string, string[]> = {};
      beatsData.forEach(beat => {
        const storedPreferences = licenseData?.filter(pref => pref.beat_id === beat.id && pref.is_enabled);
        if (storedPreferences && storedPreferences.length > 0) {
          initialLicenseTypes[beat.id] = storedPreferences.map(pref => pref.license_type);
        } else {
          // Default to all available licenses if no preferences stored
          initialLicenseTypes[beat.id] = getAllAvailableLicenses(beat);
        }
      });
      setLicenseTypes(initialLicenseTypes);
    }
  };

  const getAllAvailableLicenses = (beat: Beat) => {
    const licenses = [];
    if (beat.mp3_lease_price !== null && beat.mp3_lease_price > 0) licenses.push("mp3_lease");
    if (beat.wav_lease_price !== null && beat.wav_lease_price > 0) licenses.push("wav_lease");
    if (beat.flp_price !== null && beat.flp_price > 0) licenses.push("flp");
    if (beat.semi_exclusive_price !== null && beat.semi_exclusive_price > 0) licenses.push("semi_exclusive");
    if (beat.full_exclusive_price !== null && beat.full_exclusive_price > 0) licenses.push("full_exclusive");
    return licenses;
  };

  const handleFeaturedToggle = (beatId: string) => {
    if (featuredBeats.includes(beatId)) {
      setFeaturedBeats(prev => prev.filter(id => id !== beatId));
      setLicenseTypes(prev => {
        const newTypes = { ...prev };
        delete newTypes[beatId];
        return newTypes;
      });
    } else {
      if (featuredBeats.length < 3) {
        setFeaturedBeats(prev => [...prev, beatId]);
        const beat = beats.find(b => b.id === beatId);
        if (beat) {
          setLicenseTypes(prev => ({
            ...prev,
            [beatId]: getAllAvailableLicenses(beat)
          }));
        }
      } else {
        toast({
          variant: "destructive",
          title: "Maximum featured beats",
          description: "You can only have 3 featured beats at a time.",
        });
      }
    }
  };

  const handleLicenseToggle = (beatId: string, license: string) => {
    setLicenseTypes(prev => ({
      ...prev,
      [beatId]: prev[beatId]?.includes(license) 
        ? prev[beatId].filter(l => l !== license)
        : [...(prev[beatId] || []), license]
    }));
  };

  const handleToggleHidden = async (beat: Beat) => {
    try {
      const newValue = !beat.is_hidden;
      const { error } = await supabase
        .from('beats')
        .update({ is_hidden: newValue })
        .eq('id', beat.id);
      if (error) throw error;
      setBeats(prev => prev.map(b => b.id === beat.id ? { ...b, is_hidden: newValue } : b));
      toast({
        title: newValue ? 'Beat hidden' : 'Beat unhidden',
        description: `${beat.title} is now ${newValue ? 'hidden' : 'visible'} in store.`,
      });
    } catch (e) {
      if (import.meta.env.DEV) console.error('Toggle hidden error', e);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to toggle hidden status.' });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update all beats to unfeatured first
      await supabase
        .from("beats")
        .update({ is_featured: false })
        .neq("id", "00000000-0000-0000-0000-000000000000");

      // Update selected beats to featured
      if (featuredBeats.length > 0) {
        await supabase
          .from("beats")
          .update({ is_featured: true })
          .in("id", featuredBeats);
      }

      // Save license type preferences for all beats
      for (const beat of beats) {
        const beatLicenses = licenseTypes[beat.id] || [];
        const allAvailableLicenses = getAllAvailableLicenses(beat);
        
        // Delete existing preferences for this beat
        await supabase
          .from("beat_license_preferences")
          .delete()
          .eq("beat_id", beat.id);
        
        // Insert new preferences
        for (const license of allAvailableLicenses) {
          await supabase
            .from("beat_license_preferences")
            .insert({
              beat_id: beat.id,
              license_type: license,
              is_enabled: beatLicenses.includes(license)
            });
        }
      }

      toast({
        title: "Success",
        description: "Featured beats and license preferences updated successfully!",
      });

      if (onBeatsUpdated) {
        onBeatsUpdated();
      }
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update featured beats and license preferences.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getLicenseDisplayName = (license: string) => {
    const names: Record<string, string> = {
      mp3_lease: "MP3 Lease",
      wav_lease: "WAV Lease", 
      flp: "FLP/Stems",
      semi_exclusive: "Semi Exclusive",
      full_exclusive: "Full Exclusive"
    };
    return names[license] || license;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            Manage Featured Beats
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select up to 3 beats to feature on the homepage and choose which license types to offer for each beat.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {beats.map((beat) => (
              <Card key={beat.id} className={`transition-all ${featuredBeats.includes(beat.id) ? 'ring-2 ring-primary' : ''} ${beat.is_hidden ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm truncate">{beat.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleHidden(beat)}
                      >
                        {beat.is_hidden ? (
                          <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Unhide</span>
                        ) : (
                          <span className="flex items-center gap-1"><EyeOff className="h-3.5 w-3.5" /> Hide</span>
                        )}
                      </Button>
                      <Checkbox
                        checked={featuredBeats.includes(beat.id)}
                        onCheckedChange={() => handleFeaturedToggle(beat.id)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{beat.producer}</p>
                </CardHeader>
                
                {beat.image_url && (
                  <div className="px-4 pb-2">
                    <img 
                      src={beat.image_url} 
                      alt={beat.title}
                      className="w-full h-20 object-cover rounded"
                    />
                  </div>
                )}

                <CardContent className="pt-2">
                  <p className="text-xs font-medium mb-2">License Types to Sell:</p>
                  <div className="space-y-1">
                    {getAllAvailableLicenses(beat).map((license) => (
                      <div key={license} className="flex items-center space-x-2">
                        <Checkbox
                          id={`${beat.id}-${license}`}
                          checked={licenseTypes[beat.id]?.includes(license) || false}
                          onCheckedChange={() => handleLicenseToggle(beat.id, license)}
                        />
                        <label 
                          htmlFor={`${beat.id}-${license}`} 
                          className="text-xs cursor-pointer"
                        >
                          {getLicenseDisplayName(license)}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Featured beats: {featuredBeats.length}/3
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};